/**
 * Where a calibration lives: in memory, on the sitting, and nowhere else.
 *
 * GDS §13 says "store session-only", and the recon storage watch makes any proposal to persist
 * calibration state un-reviewed ground (D-031's `GOVERNED_RECORDS` allowlist would erase it
 * anyway). So this is a `Map` with a lifetime — the camera session holds one, `dispose()` clears
 * it, and a reload starts a child's hero training again. That is the intended behaviour, not a
 * missing feature: a calibration is a fact about one body, one room and one afternoon.
 *
 * It also owns the reuse question, so that "may I use the calibration I already have?" is asked
 * of one object rather than re-derived at each call site (architecture §9's `Check reusable
 * calibration` step).
 */

import type { CalibrationProfile } from './calibration-profile';
import {
  evaluateCalibrationReuse,
  type CalibrationRequirement,
  type CalibrationReuseRefusal,
} from './calibration-reuse';

export interface CalibrationResolution {
  /** The profile to use, or null when a fresh calibration is needed. */
  readonly profile: CalibrationProfile | null;
  readonly reuse: boolean;
  readonly refusals: readonly CalibrationReuseRefusal[];
}

export interface CalibrationStore {
  get(playerId: string): CalibrationProfile | null;
  save(profile: CalibrationProfile): void;
  /** Forgets one player's calibration, or everybody's. */
  clear(playerId?: string): void;
  resolve(requirement: CalibrationRequirement): CalibrationResolution;
  readonly size: number;
}

export function createCalibrationStore(): CalibrationStore {
  const profiles = new Map<string, CalibrationProfile>();

  return {
    get(playerId: string): CalibrationProfile | null {
      return profiles.get(playerId) ?? null;
    },

    save(profile: CalibrationProfile): void {
      // One profile per player: a second sitting replaces the first rather than leaving two
      // versions of a child's body for something else to choose between.
      profiles.set(profile.playerId, profile);
    },

    clear(playerId?: string): void {
      if (playerId === undefined) profiles.clear();
      else profiles.delete(playerId);
    },

    resolve(requirement: CalibrationRequirement): CalibrationResolution {
      const held = profiles.get(requirement.playerId) ?? null;
      const decision = evaluateCalibrationReuse(held, requirement);

      return {
        profile: decision.reuse ? held : null,
        reuse: decision.reuse,
        refusals: decision.refusals,
      };
    },

    get size(): number {
      return profiles.size;
    },
  };
}
